<script lang="ts">
  import { createQuery, createMutation, useQueryClient } from '@tanstack/svelte-query';
  import { KeyRound, Lock, History, Trash2, Plus, Check, Users } from 'lucide-svelte';
  import { adminUsersApi, adminLoginsApi } from '$lib/api/admin';
  import { projectsApi } from '$lib/api/projects';
  import { getErrorMessage } from '$lib/api/client';
  import { relTime, teamKind, teamLabel } from '$lib/utils/format';
  import { userHasProject } from '$lib/utils/projects';
  import Modal from '$lib/components/ui/Modal.svelte';
  import Avatar from '$lib/components/ui/Avatar.svelte';
  import Chip from '$lib/components/ui/Chip.svelte';
  import EmptyState from '$lib/components/ui/EmptyState.svelte';
  import UserActivityModal from './UserActivityModal.svelte';
  import type { AdminUser, Team } from '$lib/types';
  import { TEAMS } from '$lib/types';

  const queryClient = useQueryClient();

  let showForm = $state(false);
  let editingId = $state<number | null>(null);
  let formError = $state('');
  let tempPassword = $state('');

  let resetPasswordUser = $state<AdminUser | null>(null);
  let resetPasswordTempPassword = $state('');
  let activityUser = $state<AdminUser | null>(null);

  function emptyForm() {
    return {
      email: '',
      name: '',
      role: 'member' as 'admin' | 'member',
      team: 'fullstack' as Team,
      project_id: undefined as number | undefined,
      projects: [] as number[]
    };
  }
  let form = $state(emptyForm());

  const usersQuery = createQuery(() => ({
    queryKey: ['admin-users', { search: '' }],
    queryFn: () => adminUsersApi.list({ per_page: 100 })
  }));

  const projectsQuery = createQuery(() => ({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list()
  }));

  // Teams are a hard boundary (no grant may cross them — see
  // internal/models/team.go), so for a member the primary-project select
  // and additional-projects checklist only ever offer projects in the
  // selected team; picking a different team clears anything that no longer
  // qualifies.
  const teamProjects = $derived((projectsQuery.data?.results ?? []).filter((p) => p.team === form.team));

  function projectNames(row: AdminUser): string {
    const names = (projectsQuery.data?.results ?? []).filter((p) => userHasProject(row, p.id)).map((p) => p.name);
    return names.length ? names.join(', ') : '—';
  }

  function formatWhen(iso: string | null): string {
    if (!iso) return 'Never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return relTime(d);
  }

  const saveMutation = createMutation(() => ({
    mutationFn: (body: Record<string, unknown>) =>
      editingId ? adminUsersApi.update(editingId, body) : adminUsersApi.create(body),
    onSuccess: (data: AdminUser) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      if (data.temp_password) {
        tempPassword = data.temp_password;
      } else {
        closeForm();
      }
    },
    onError: (err: unknown) => (formError = getErrorMessage(err))
  }));

  const deleteMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetTotpMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetTotp(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-users'] })
  }));

  const resetPasswordMutation = createMutation(() => ({
    mutationFn: (id: number) => adminUsersApi.resetPassword(id),
    onSuccess: (data: { temp_password: string }) => {
      resetPasswordTempPassword = data.temp_password;
    },
    onError: (err: unknown) => {
      resetPasswordUser = null;
      alert(getErrorMessage(err));
    }
  }));

  function openCreate() {
    editingId = null;
    form = emptyForm();
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  function openEdit(row: AdminUser) {
    editingId = row.id;
    form = {
      email: row.email,
      name: row.name,
      role: row.role,
      team: row.team ?? 'fullstack',
      project_id: row.project_id ?? undefined,
      projects: [...row.projects]
    };
    formError = '';
    tempPassword = '';
    showForm = true;
  }

  // Switching team invalidates any primary project / additional grants that
  // no longer match (the backend rejects them anyway — see admin_users.go's
  // cross-team checks); dropping them here keeps the form from submitting a
  // combination that's guaranteed to 400.
  function onTeamChange() {
    if (form.project_id !== undefined && !teamProjects.some((p) => p.id === form.project_id)) {
      form.project_id = undefined;
    }
    form.projects = form.projects.filter((id) => teamProjects.some((p) => p.id === id));
  }

  const loginsQuery = createQuery(() => ({
    queryKey: ['admin-logins-recent'],
    queryFn: () => adminLoginsApi.recent({ limit: 100 })
  }));

  function closeForm() {
    showForm = false;
    tempPassword = '';
  }

  function submit(e: SubmitEvent) {
    e.preventDefault();
    if (!editingId && !form.email.trim()) {
      formError = 'Email is required';
      return;
    }
    if (form.role === 'member' && !form.team) {
      formError = 'Team is required for members';
      return;
    }
    formError = '';
    const body: Record<string, unknown> = { ...form };
    if (editingId) delete body.email; // email is immutable after creation
    if (form.role === 'admin') delete body.team; // admins have no team
    saveMutation.mutate(body);
  }

  function toggleProject(id: number) {
    const idx = form.projects.indexOf(id);
    if (idx >= 0) form.projects.splice(idx, 1);
    else form.projects.push(id);
  }

  function remove(row: AdminUser) {
    if (confirm(`Delete user "${row.email}"?`)) deleteMutation.mutate(row.id);
  }

  function resetTotp(row: AdminUser) {
    if (confirm(`Reset two-factor for "${row.email}"? They will re-enroll at next login.`)) {
      resetTotpMutation.mutate(row.id);
    }
  }

  function resetPassword(row: AdminUser) {
    if (confirm(`Reset password for "${row.email}"? Their existing sessions will be signed out immediately.`)) {
      resetPasswordUser = row;
      resetPasswordTempPassword = '';
      resetPasswordMutation.mutate(row.id);
    }
  }

  function closeResetPassword() {
    resetPasswordUser = null;
    resetPasswordTempPassword = '';
  }

  function openActivity(row: AdminUser) {
    activityUser = row;
  }

  function closeActivity() {
    activityUser = null;
  }
</script>

<div class="page-head">
  <div>
    <h1 class="h-title">Users &amp; access</h1>
    <div class="h-sub">{usersQuery.data?.count ?? 0} accounts · role &amp; project grants</div>
  </div>
  <span class="spacer"></span>
  <button type="button" class="btn btn-primary" onclick={openCreate}><Plus size={16} /> Add user</button>
</div>

{#if usersQuery.isLoading}
  <p style="color:var(--t3);font-size:13.5px">Loading users…</p>
{:else if usersQuery.isError}
  <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(usersQuery.error)}</p>
{:else if usersQuery.data?.results.length}
  <div class="card table-scroll">
    <table class="table">
      <thead>
        <tr>
          <th>User</th>
          <th>Role</th>
          <th>Team</th>
          <th>Projects</th>
          <th>2FA</th>
          <th>Last active</th>
          <th style="text-align:right">Actions</th>
        </tr>
      </thead>
      <tbody>
        {#each usersQuery.data.results as row (row.id)}
          <tr class="clickable" onclick={() => openEdit(row)}>
            <td>
              <div style="display:flex;align-items:center;gap:11px">
                <Avatar id={row.id} name={row.name || row.email} size={32} />
                <div>
                  <div style="font-size:13.5px;font-weight:600;color:var(--t1)">{row.name || row.email}</div>
                  <div style="font-size:11.5px;color:var(--t3)">{row.email}</div>
                </div>
              </div>
            </td>
            <td><Chip label={row.role} kind={row.role === 'admin' ? 'teal' : 'gray'} /></td>
            <td>{#if row.team}<Chip label={teamLabel(row.team)} kind={teamKind(row.team)} />{:else}<span style="color:var(--t3)">—</span>{/if}</td>
            <td>{projectNames(row)}</td>
            <td>{row.totp_enabled ? 'Enrolled' : 'Not enrolled'}</td>
            <td>{formatWhen(row.last_active)}</td>
            <td style="text-align:right" onclick={(e) => e.stopPropagation()}>
              <div style="display:flex;justify-content:flex-end;gap:6px">
                <button type="button" class="icon-btn" onclick={() => openActivity(row)} aria-label="Login activity" title="Login activity">
                  <History size={16} />
                </button>
                <button type="button" class="icon-btn" onclick={() => resetPassword(row)} aria-label="Reset password" title="Reset password">
                  <Lock size={16} />
                </button>
                <button type="button" class="icon-btn" onclick={() => resetTotp(row)} aria-label="Reset two-factor" title="Reset two-factor">
                  <KeyRound size={16} />
                </button>
                <button type="button" class="btn btn-danger btn-sm" onclick={() => remove(row)} aria-label="Delete" title="Delete">
                  <Trash2 size={16} />
                </button>
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>

  <h2 class="h-title" style="font-size:16px;margin-top:32px;margin-bottom:2px">Login activity</h2>
  <div class="h-sub" style="margin-bottom:12px">Global feed · time, IP, device</div>
  {#if loginsQuery.isLoading}
    <p style="color:var(--t3);font-size:13.5px">Loading logins…</p>
  {:else if loginsQuery.isError}
    <p style="color:var(--danger);font-size:13.5px">{getErrorMessage(loginsQuery.error)}</p>
  {:else if loginsQuery.data}
    <div class="card table-scroll">
      <table class="table">
        <thead>
          <tr>
            <th>User</th>
            <th>When</th>
            <th>IP address</th>
            <th>Device</th>
          </tr>
        </thead>
        <tbody>
          {#each loginsQuery.data.results as row (row.id)}
            <tr>
              <td style="color:var(--t1);font-weight:500">{row.email ?? `#${row.user_id}`}</td>
              <td>{new Date(row.created_at).toLocaleString()}</td>
              <td class="mono">{row.ip_address || '—'}</td>
              <td><Chip label={`${row.browser} · ${row.os} · ${row.device}`} kind="gray" /></td>
            </tr>
          {:else}
            <tr><td colspan="4" style="text-align:center;color:var(--t3);padding:32px">No logins recorded yet.</td></tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}
{:else}
  <EmptyState icon={Users} heading="No users yet" sub="Add your team so they can sign in and get granted project access.">
    {#snippet action()}
      <button type="button" class="btn btn-primary" onclick={openCreate}><Plus size={16} /> Add user</button>
    {/snippet}
  </EmptyState>
{/if}

{#if showForm}
  <Modal title={editingId ? 'Edit user' : 'New user'} onClose={closeForm} footer={tempPassword ? undefined : formFooter}>
    {#if tempPassword}
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="border-radius:8px;background:var(--chip-warn-bg);color:var(--chip-warn-fg);padding:11px 13px;font-size:13px">
          Share this temporary password with the user. It won't be shown again.
        </div>
        <p class="mono" style="font-size:16px;text-align:center;background:var(--surface-2);border-radius:8px;padding:12px">{tempPassword}</p>
        <button type="button" class="btn btn-primary" style="justify-content:center" onclick={closeForm}>Done</button>
      </div>
    {:else}
      <form id="user-form" onsubmit={submit}>
        <div class="field">
          <label class="label" for="email">Email</label>
          <input id="email" type="email" class="input" bind:value={form.email} disabled={!!editingId} />
        </div>
        <div class="field">
          <label class="label" for="name">Name</label>
          <input id="name" class="input" bind:value={form.name} />
        </div>
        <div class="field">
          <label class="label" for="role">Role</label>
          <select id="role" class="select" bind:value={form.role}>
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        {#if form.role === 'member'}
          <div class="field">
            <label class="label" for="team">Team</label>
            <select id="team" class="select" bind:value={form.team} onchange={onTeamChange}>
              {#each TEAMS as t (t)}
                <option value={t}>{teamLabel(t)}</option>
              {/each}
            </select>
            <div style="font-size:11.5px;color:var(--t3);margin-top:4px">
              Teams are a hard boundary — this member's primary project and grants can only ever be within their team.
            </div>
          </div>
        {/if}
        <div class="field">
          <label class="label" for="project_id">Primary project</label>
          <select id="project_id" class="select" bind:value={form.project_id}>
            <option value={undefined}>-- none --</option>
            {#each (form.role === 'member' ? teamProjects : (projectsQuery.data?.results ?? [])) as p (p.id)}
              <option value={p.id}>{p.name}</option>
            {/each}
          </select>
        </div>
        <div class="field">
          <span class="label">Additional projects</span>
          {#each (form.role === 'member' ? teamProjects : (projectsQuery.data?.results ?? [])) as p (p.id)}
            {@const on = form.projects.includes(p.id)}
            <div class="grant-row {on ? 'on' : ''}" role="button" tabindex="0" onclick={() => toggleProject(p.id)} onkeydown={(e) => e.key === 'Enter' && toggleProject(p.id)}>
              <span class="checkbox">{#if on}<Check size={13} strokeWidth={3} />{/if}</span>
              <span style="font-size:13.5px;color:var(--t1);font-weight:500">{p.name}</span>
            </div>
          {/each}
          {#if form.role === 'member' && teamProjects.length < (projectsQuery.data?.results.length ?? 0)}
            <div style="font-size:11.5px;color:var(--t3);margin-top:4px">
              {(projectsQuery.data?.results.length ?? 0) - teamProjects.length} project(s) hidden — different team.
            </div>
          {/if}
        </div>
        {#if formError}<p style="color:var(--danger);font-size:13px">{formError}</p>{/if}
      </form>
    {/if}
  </Modal>
{/if}

{#snippet formFooter()}
  <span class="spacer"></span>
  <button type="button" class="btn btn-secondary" onclick={closeForm}>Cancel</button>
  <button type="submit" form="user-form" class="btn btn-primary" disabled={saveMutation.isPending}>
    {saveMutation.isPending ? 'Saving…' : 'Save'}
  </button>
{/snippet}

{#if resetPasswordUser}
  <Modal title="Reset password" onClose={closeResetPassword}>
    {#if resetPasswordTempPassword}
      <div style="display:flex;flex-direction:column;gap:14px">
        <div style="border-radius:8px;background:var(--chip-warn-bg);color:var(--chip-warn-fg);padding:11px 13px;font-size:13px">
          Share this temporary password with {resetPasswordUser.email}. It won't be shown again. Their existing sessions have
          been signed out.
        </div>
        <p class="mono" style="font-size:16px;text-align:center;background:var(--surface-2);border-radius:8px;padding:12px">{resetPasswordTempPassword}</p>
        <button type="button" class="btn btn-primary" style="justify-content:center" onclick={closeResetPassword}>Done</button>
      </div>
    {:else}
      <p style="color:var(--t3);font-size:13px">Resetting password…</p>
    {/if}
  </Modal>
{/if}

{#if activityUser}
  <UserActivityModal user={activityUser} onClose={closeActivity} />
{/if}
